import { ref, computed, watch } from "vue";

export interface ActionData {
  type:
    | "cluster_create"
    | "cluster_delete"
    | "cluster_rename"
    | "cluster_move"
    | "keywords_move"
    | "keywords_collect";
  description: string; // Legacy field for backward compatibility
  descriptionKey?: string; // Translation key
  descriptionParams?: Record<string, any>; // Translation parameters
  timestamp: number;
  // Данные, специфичные для каждого типа действия
  payload: {
    clusterId?: number;
    clusterName?: string;
    oldName?: string;
    newName?: string;
    oldParentId?: number | null;
    newParentId?: number | null;
    keywordIds?: number[];
    sourceClusterId?: number;
    targetClusterId?: number;
  };
}

const MAX_HISTORY_SIZE = 10;
const STORAGE_KEY = "semantica_undo_history";
const STORAGE_KEY_REDO = "semantica_redo_history";

export function useUndoRedoWithStorage() {
  // Загружаем историю из localStorage при инициализации
  const loadHistory = (): ActionData[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Failed to load undo history:", error);
      return [];
    }
  };

  const loadRedoHistory = (): ActionData[] => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_REDO);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error("Failed to load redo history:", error);
      return [];
    }
  };

  const undoHistory = ref<ActionData[]>(loadHistory());
  const redoHistory = ref<ActionData[]>(loadRedoHistory());

  const canUndo = computed(() => undoHistory.value.length > 0);
  const canRedo = computed(() => redoHistory.value.length > 0);

  const lastAction = computed(() =>
    undoHistory.value.length > 0
      ? undoHistory.value[undoHistory.value.length - 1]
      : null,
  );

  const nextAction = computed(() =>
    redoHistory.value.length > 0
      ? redoHistory.value[redoHistory.value.length - 1]
      : null,
  );

  // Сохраняем историю в localStorage при изменении
  watch(
    undoHistory,
    (newHistory) => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newHistory));
      } catch (error) {
        console.error("Failed to save undo history:", error);
      }
    },
    { deep: true },
  );

  watch(
    redoHistory,
    (newHistory) => {
      try {
        localStorage.setItem(STORAGE_KEY_REDO, JSON.stringify(newHistory));
      } catch (error) {
        console.error("Failed to save redo history:", error);
      }
    },
    { deep: true },
  );

  function addAction(action: ActionData) {
    // Добавляем действие в историю
    undoHistory.value.push(action);

    // Ограничиваем размер истории
    if (undoHistory.value.length > MAX_HISTORY_SIZE) {
      undoHistory.value.shift();
    }

    // Очищаем стек повтора при новом действии
    redoHistory.value = [];
  }

  // Функция выполнения будет установлена позже для избежания циклической зависимости
  let executeActionFn:
    | ((action: ActionData, isUndo: boolean) => Promise<void>)
    | null = null;

  function setExecuteAction(
    fn: (action: ActionData, isUndo: boolean) => Promise<void>,
  ) {
    executeActionFn = fn;
  }

  async function undo() {
    if (!canUndo.value || !executeActionFn) return;

    const action = undoHistory.value.pop();
    if (!action) return;

    try {
      // Выполняем откат действия
      await executeActionFn(action, true);

      // Перемещаем действие в стек повтора
      redoHistory.value.push(action);
    } catch (error) {
      console.error("Failed to undo action:", error);
      // Возвращаем действие обратно в стек отмены
      undoHistory.value.push(action);
      throw error;
    }
  }

  async function redo() {
    if (!canRedo.value || !executeActionFn) return;

    const action = redoHistory.value.pop();
    if (!action) return;

    try {
      // Повторяем действие
      await executeActionFn(action, false);

      // Перемещаем действие обратно в стек отмены
      undoHistory.value.push(action);
    } catch (error) {
      console.error("Failed to redo action:", error);
      // Возвращаем действие обратно в стек повтора
      redoHistory.value.push(action);
      throw error;
    }
  }

  function clearHistory() {
    undoHistory.value = [];
    redoHistory.value = [];
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY_REDO);
  }

  return {
    undoHistory,
    redoHistory,
    canUndo,
    canRedo,
    lastAction,
    nextAction,
    addAction,
    undo,
    redo,
    clearHistory,
    setExecuteAction,
  };
}

// Глобальный экземпляр для использования во всем приложении
const globalUndoRedo = useUndoRedoWithStorage();

export function useGlobalUndoRedo() {
  return globalUndoRedo;
}
